/** ***********************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 ************************************************************************* */

import {
    App, AppAction, AppSectionBasedField, Progress, AppLabel, AppStatusLabel,
} from '../../../stores/app';
import {
    APP_MODE, ENTITLEMENTS, UPDATE_TYPE, ACTIVE_SESSIONS_STATUS, TRIAL_STATUS,
    ACTION_IN_PROGRESS,
    MODIFYIING_OPERATION_TYPES,
    LICENSE_MODE,
} from './appJsonConstants';
import {
    APP_PLATFORM, LABEL_VARIANT_TYPE,
    ICON_VARIANT_TYPE, PLATFORM_FILTERS,
    DESKTOP_APP_FILTERS, UPDATES_APP_FILTERS, ACTION_ID,
    SUPPORTED_OS, PROGRESS_UPDATE_CTA_ID,
} from './appModelConstants';
import { APPS_CONFIG } from './appsConfig';
import TranslationUtil from '../../translation';
import {
    LEFT_NAV_KEYS, APPINFO_ACTION_ID, APPS_COMPONENT_ID, CATEGORY_IDS, LABEL_PRIORITY_TYPES,
} from '../../enums';
import PDPs from '../../../stores/pdps';
import appConfig from '../../../stores/config';
import pdpModelMapper from './pdpModelMapper';
import ProgressBarAnalytics from '../../../analytics/progressBarAnalytics';
import LoggerUtil from '../../LoggerUtil';

const COMPONENT_ID = 'AppModelMapper';
const TILE_ACTION = {
    START_TRIAL: {
        actionId: ACTION_ID.START_TRIAL,
        textId: 'ccx.appAction.trial',
    },
    LEARN_MORE: {
        actionId: ACTION_ID.LEARN_MORE,
        textId: 'ccx.appAction.learnMore',
    },
    DISCOVER: {
        actionId: ACTION_ID.DISCOVER,
        textId: 'ccx.appAction.discover',
        hidden: true,
    },
    OPEN: {
        actionId: ACTION_ID.OPEN,
        textId: 'ccx.appAction.open',
    },
    NOT_COMPATIBLE: {
        actionId: ACTION_ID.NOT_COMPATIBLE,
        textId: 'ccx.appAction.notCompatible',
        iconType: 'error',
    },
    BUY_NOW: {
        actionId: ACTION_ID.BUY_NOW,
        textId: 'ccx.appAction.buyNow',
    },
    BUY: {
        actionId: ACTION_ID.BUY,
        textId: 'ccx.appAction.buy',
    },
    LIST_OTHER_VERSIONS: {
        actionId: ACTION_ID.LIST_OTHER_VERSIONS,
        textId: 'ccx.appAction.listOtherVersions',
    },
    UNINSTALL: {
        actionId: ACTION_ID.UNINSTALL,
        textId: 'ccx.appAction.uninstall',
        textType: 'error',
    },
    UPDATE: {
        actionId: ACTION_ID.UPDATE,
        textId: 'ccx.appAction.update',
    },
    INSTALL: {
        actionId: ACTION_ID.INSTALL,
        textId: 'ccx.appAction.install',
    },
    TRY: {
        actionId: ACTION_ID.TRY,
        textId: 'ccx.appAction.try',
    },
    LAUNCH_SERVICE: {
        actionId: ACTION_ID.LAUNCH_SERVICE,
        textId: 'ccx.appAction.launchService',
    },
    GET_MOBILE_LINK: {
        actionId: ACTION_ID.GET_LINK,
        textId: 'ccx.appAction.sendMobileLink',
    },
    RETRY: {
        actionId: ACTION_ID.RETRY,
        textId: 'ccx.appAction.retry',
    },
    SEE_MORE: {
        actionId: ACTION_ID.SEE_MORE,
        textId: 'ccx.update.description.viewMore',
    },
};

const DISABLED_ACTIONS_IN_PROGRESS = [
    ACTION_ID.START_TRIAL,
    ACTION_ID.OPEN,
    ACTION_ID.NOT_COMPATIBLE,
    ACTION_ID.RETRY,
    ACTION_ID.TRY,
    ACTION_ID.UNINSTALL,
    ACTION_ID.INSTALL,
    ACTION_ID.UPDATE,
    ACTION_ID.LIST_OTHER_VERSIONS,
];

const MAX_BUTTON_LIST_TEXT_WIDTH = 155;
const MAX_BUTTON_LIST_TEXT_WIDTH_S = 139;

class AppModelMapper {
    getTileAction({
        actionId, textId, iconType, hidden, textType,
    }) {
        const localizedText = TranslationUtil.getMessage(textId);
        return {
            actionId, localizedText, iconType, hidden, textType,
        };
    }

    getAppPlatform({ mode }) {
        let appPlatform = APP_PLATFORM.DESKTOP;
        if (mode === APP_MODE.MOBILE_APPS) {
            appPlatform = APP_PLATFORM.MOBILE;
        }
        if (mode === APP_MODE.WEB_APPS) {
            appPlatform = APP_PLATFORM.WEB;
        }
        return appPlatform;
    }

    isPropertyPresent(modelObj, propertyName) {
        return modelObj
            && Object.prototype.hasOwnProperty.call(modelObj, propertyName);
    }

    getSupportedOS({ mode, appData }) {
        let supportedOS = [];
        if (mode === APP_MODE.MOBILE_APPS
            && (this.isPropertyPresent(appData, 'supported_OS_list'))) {
            supportedOS = appData.supported_OS_list.map((os) => {
                switch (os) {
                case 'iOS':
                    return SUPPORTED_OS.APPLE;
                case 'Android':
                    return SUPPORTED_OS.ANDROID;
                case 'Windows':
                    return SUPPORTED_OS.WINDOWS;
                default:
                    return os;
                }
            });
        }
        return supportedOS;
    }

    getSortIndex(appData) {
        let sortIndex = 100000;
        if (this.isPropertyPresent(appData, 'sort_order')) {
            sortIndex = parseInt(appData.sort_order, 10);
        }
        return sortIndex;
    }

    getSortOrder({ appData }) {
        const sortIndex = this.getSortIndex(appData);
        const sortOrder = {};
        sortOrder[LEFT_NAV_KEYS.ALL_APPS_KEY] = sortIndex;
        sortOrder[LEFT_NAV_KEYS.UPDATES_KEY] = sortIndex;

        if (this.isPropertyPresent(appData, 'categories')) {
            let categoriesData = [];
            if (appData.categories && Array.isArray(appData.categories)) {
                categoriesData = appData.categories;
            }
            categoriesData.map((obj) => {
                sortOrder[obj.name] = parseInt(obj.sort_order, 10);
                return obj.name;
            });
        }
        return sortOrder;
    }

    getAppIcon({ appData }) {
        // TODO: need handling for backup url
        let appIconURL = '';
        if (this.isPropertyPresent(appData, 'icon')) {
            appIconURL = appData.icon;
        }
        return appIconURL;
    }

    getBackUpIcon({ appData }) {
        /* eslint-disable max-len */
        let backUpIcon = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MiIgaGVpZ2h0PSI0MiIgdmlld0JveD0iMCAwIDQyIDQyIj4KICA8ZyBpZD0iR3JvdXBfNzI0MzAiIGRhdGEtbmFtZT0iR3JvdXAgNzI0MzAiIHRyYW5zZm9ybT0idHJhbnNsYXRlKC0zODE2IC00NTI3KSI+CiAgICA8cmVjdCBpZD0iUmVjdGFuZ2xlXzE0NDQ2NSIgZGF0YS1uYW1lPSJSZWN0YW5nbGUgMTQ0NDY1IiB3aWR0aD0iNDIiIGhlaWdodD0iNDIiIHRyYW5zZm9ybT0idHJhbnNsYXRlKDM4MTYgNDUyNykiIGZpbGw9IiNlYWVhZWEiLz4KICAgIDxnIGlkPSJHcm91cF83MjQxNiIgZGF0YS1uYW1lPSJHcm91cCA3MjQxNiIgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoMzgyNSA0NTM2KSI+CiAgICAgIDxyZWN0IGlkPSJDYW52YXMiIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgZmlsbD0iI2ZmMTNkYyIgb3BhY2l0eT0iMCIvPgogICAgICA8cGF0aCBpZD0iUGF0aF84MjU4NiIgZGF0YS1uYW1lPSJQYXRoIDgyNTg2IiBkPSJNMTAuNDQ3LDcuMjgzYTUuOTM2LDUuOTM2LDAsMCwxLDEuMDM5LjUzNyw0LjkzLDQuOTMsMCwxLDEtLjQxOCw3LjM0MmwtMi44ODEtMi44OWEuNTQzLjU0MywwLDEsMC0uNzc5Ljc1OGwyLjksMi45YTYuMDE5LDYuMDE5LDAsMSwwLC4xNDQtOC42NTJaTTMuMzcsMTIuODM2QTQuODY1LDQuODY1LDAsMCwwLDguMjM2LDE3LjdoMi41NDNhNy4xLDcuMSwwLDAsMS0xLjMzOC0xLjA4NGgtMS4yYTMuNzgxLDMuNzgxLDAsMSwxLDAtNy41NjIsMy42LDMuNiwwLDAsMSwyLjQ4LjkyOGwyLjE4OSwyLjI0NmEuNTQzLjU0MywwLDAsMCwuNzc4LS43NThMMTEuNSw5LjIyMUE0LjcwOCw0LjcwOCwwLDAsMCw4LjIzMyw3Ljk2Nyw0Ljg2Myw0Ljg2MywwLDAsMCwzLjM3LDEyLjgzMloiIGZpbGw9IiNiM2IzYjMiLz4KICAgICAgPHBhdGggaWQ9IlBhdGhfODI1ODciIGRhdGEtbmFtZT0iUGF0aCA4MjU4NyIgZD0iTTE0LjU1NSw0LjE2QTcuNTE5LDcuNTE5LDAsMCwxLDE1LjgsMTkuMDkycy0xLjAwNy4xMDgtMS44MjkuMTA4SDguMjM3YTYuMzY3LDYuMzY3LDAsMSwxLDAtMTIuNzMzLDYuNTc2LDYuNTc2LDAsMCwxLC44MzQuMDUzbC4zNTUtLjMzM0E3LjQ4OCw3LjQ4OCwwLDAsMSwxNC41NTUsNC4xNlptMC0uOEE4LjI4Myw4LjI4MywwLDAsMCw4Ljg3Niw1LjZsLS4wOTEuMDg1Yy0uMTgzLS4wMTQtLjM2OC0uMDIxLS41NTItLjAyMUE3LjE2OCw3LjE2OCwwLDEsMCw4LjIzNywyMGg1LjczOGMuODU1LDAsMS44NzEtLjEwOCwxLjkxNC0uMTEzQTguMzE4LDguMzE4LDAsMCwwLDE0LjU1NSwzLjM2WiIgZmlsbD0iI2IzYjNiMyIvPgogICAgPC9nPgogIDwvZz4KPC9zdmc+Cg==';
        if (this.isPropertyPresent(appData, 'backup_icon')) {
            if (appData.backup_icon) {
                backUpIcon = appData.backup_icon;
            }
        }
        return backUpIcon;
    }

    getIncompatibilityDetails({ mode, appData }) {
        let isIncompatible = false;
        let incompatibilityReason = '';
        if (mode === APP_MODE.INCOMPATIBLE_APPS) {
            isIncompatible = true;
            incompatibilityReason = appData.incompatibility_details;
        }
        return { isIncompatible, incompatibilityReason };
    }

    getSystemRequirementURL({ appData }) {
        let systemRequirementsURL = appData['system-requirements-url'];
        if (!systemRequirementsURL) {
            systemRequirementsURL = TranslationUtil.getMessage('ccx.url.systemRequirements');
        }
        return systemRequirementsURL;
    }

    getLocalizedTagline(appData) {
        let tagline = '';
        if (this.isPropertyPresent(appData, 'product_tagline')) {
            tagline = appData.product_tagline;
        }
        return tagline;
    }

    getLocalizedUpdateDescription(appData) {
        let localizedString = '';
        if (this.isPropertyPresent(appData, 'updates_info')) {
            localizedString = appData.updates_info;
        }
        return localizedString;
    }

    toAppActionTypeModel(actionType, isDisabled = false, textType = '') {
        let actionTypeModel = {};
        switch (actionType) {
        case ('quietButton'): {
            actionTypeModel = {
                quiet: false,
                variant: 'primary',
                isDisabled,
            };
            break;
        }
        case ('primaryQuietButton'): {
            actionTypeModel = {
                quiet: true,
                variant: 'primary',
                isDisabled,
            };
            break;
        }
        case ('secondaryQuietButton'): {
            actionTypeModel = {
                quiet: true,
                variant: 'secondary',
                isDisabled,
            };
            break;
        }
        case ('ctaButton'): {
            actionTypeModel = {
                quiet: false,
                variant: 'cta',
                isDisabled,
            };
            break;
        }
        case ('dropDownItem'): {
            actionTypeModel = {
                quiet: true,
                variant: 'dropDown',
                isDisabled,
                textType,
            };
            break;
        }
        default:
        }
        return actionTypeModel;
    }

    toAppActionModel(actionId, actionTypeValue, localizedText,
        iconType, isDisabled = false, hidden = false, textType = '', title = '') {
        const actionType = this.toAppActionTypeModel(actionTypeValue, isDisabled, textType);
        const appAction = new AppAction({
            actionId,
            actionType,
            localizedText,
            hidden,
            title,
        });
        if (iconType) {
            appAction.iconType = iconType;
        }
        return appAction;
    }

    shouldMoveSecondaryButtonToDropdownList(buttonList) {
        let innerHtmlStr = '';
        let shouldMove = false;
        buttonList.forEach((button) => {
            innerHtmlStr += button.localizedText;
        });
        const buttonDiv = document.createElement('div');
        buttonDiv.style.position = 'absolute';
        buttonDiv.style.visibility = 'hidden';
        buttonDiv.style.fontWeight = 700;
        buttonDiv.style.fontSize = '15px';
        buttonDiv.innerHTML = innerHtmlStr;
        document.body.appendChild(buttonDiv);
        shouldMove = (buttonDiv.offsetWidth > MAX_BUTTON_LIST_TEXT_WIDTH);
        if (appConfig.shouldWrapAtLowerBP) {
            shouldMove = (buttonDiv.offsetWidth > MAX_BUTTON_LIST_TEXT_WIDTH_S);
        }
        document.body.removeChild(buttonDiv);
        return shouldMove;
    }

    jsonToAppActionListModel(appActionJson) {
        let buttonList = [];
        let dropDownList = [];
        const linksList = [];
        Object.keys(appActionJson).forEach((actionType) => {
            appActionJson[actionType].forEach((appAction) => {
                const appActionModel = this.toAppActionModel(
                    appAction.actionId,
                    actionType,
                    appAction.localizedText,
                    appAction.iconType,
                    appAction.isDisabled,
                    appAction.hidden,
                    appAction.textType,
                );
                if (actionType === 'dropDownItem') {
                    dropDownList.push(appActionModel);
                } else if (actionType === 'linkItem') {
                    linksList.push(appActionModel);
                } else {
                    buttonList.push(appActionModel);
                }
            });
        });
        if (this.shouldMoveSecondaryButtonToDropdownList(buttonList)) {
            // NOTE: Moving secondary buttons to spillover menu to avoid overflow of buttons
            const filteredButtonList = buttonList.filter(button => (button.actionType.variant !== 'secondary'));
            const actionsToPushInDropdown = buttonList.filter(button => (button.actionType.variant === 'secondary'));
            dropDownList = actionsToPushInDropdown.concat(dropDownList);
            buttonList = filteredButtonList;
        }
        return { buttonList, dropDownList, linksList };
    }

    isUpdateAvailable({ appData }) {
        let isUpdateAvailable = false;
        if (appData.is_update_available) {
            isUpdateAvailable = appData.is_update_available === 'true';
        }
        return isUpdateAvailable;
    }

    getFamilyName({ appData }) {
        let appFamilyName = '';
        if (this.isPropertyPresent(appData, 'family_name') && appData.family_name !== '') {
            appFamilyName = appData.family_name;
        } else if (this.isPropertyPresent(appData, 'display_name')) {
            appFamilyName = appData.display_name;
        }
        return appFamilyName;
    }

    // #region Entitlement related functions

    updateTrialStatus(appModel, trialData) {
        LoggerUtil.info(`${APPS_COMPONENT_ID}: ${COMPONENT_ID}: updateTrialStatus: triggerd with data: ${JSON.stringify(trialData)}`);

        const { status, trialDetails } = trialData;
        if (status === TRIAL_STATUS.COMPLETED) {
            if (trialDetails) {
                const { isProductInTrial, isTrialExpired, trialDaysRemaining } = trialDetails;
                const { mode } = appModel;
                const appData = {
                    entitlement_info: {},
                    launchable: 'true',
                    uninstallable: appModel.isUninstallable,
                    discover_enabled: appModel.discoverEnabled.toString(),
                };
                if (isProductInTrial === 'True') {
                    appData.entitlement_info = {
                        status: ENTITLEMENTS.TRIAL_ACTIVE,
                        trialDaysRemaining,
                    };
                }
                if (isTrialExpired === 'True') {
                    appData.entitlement_info = {
                        status: ENTITLEMENTS.TRIAL_EXPIRED,
                    };
                }
                this.updateSubtext(appModel, appData);
                this.updateStatusLabels(appModel, appData, mode);
                appModel.appSectionBasedFields.forEach((appSectionBasedField) => {
                    this.updateButtonsForSectionBasedField(appSectionBasedField, appData, mode);
                });
                const actionList = this.toPDPAppActionListModel({ mode, appData });
                appModel.setActionList(actionList);
                this.updatePDPModel(appModel);
            }
        } else if (status === TRIAL_STATUS.FAILED) {
            const mode = APP_MODE.ACTIVE_SESSIONS;
            this.updateSubtext(appModel, trialData);
            this.updateErrorDetailsOnFailure(appModel, trialData);
            appModel.appSectionBasedFields.forEach((appSectionBasedField) => {
                this.updateButtonsForSectionBasedField(appSectionBasedField, appModel, mode);
            });
            appModel.resetProgress();
            const actionList = this.toPDPAppActionListModel({ mode, appData: appModel });
            appModel.setActionList(actionList);
            this.updatePDPModel(appModel, null);
        } else {
            LoggerUtil.warn(`${APPS_COMPONENT_ID}: ${COMPONENT_ID}: updateTrialStatus: Unhandled status ${status}`);
        }
    }

    getTrialDaysRemaining({ appData }) {
        let trialDaysRemaining = '0';
        if (this.isPropertyPresent(appData, 'entitlement_info')
        && this.isPropertyPresent(appData.entitlement_info, 'trialDaysRemaining')) {
            ({ trialDaysRemaining } = appData.entitlement_info);
        }
        return trialDaysRemaining;
    }

    getEntitlementStatus(appData) {
        let entitlementStatus = ENTITLEMENTS.NO_ENTITLEMENT;
        if (this.isPropertyPresent(appData, 'entitlement_info')
        && this.isPropertyPresent(appData.entitlement_info, 'status')) {
            entitlementStatus = appData.entitlement_info.status;
        }
        return entitlementStatus;
    }

    getLicenseMode(appData) {
        let appLicenseMode = LICENSE_MODE.PAID;
        if (this.isPropertyPresent(appData, 'entitlement_info')
        && this.isPropertyPresent(appData.entitlement_info, 'licenseMode')) {
            appLicenseMode = appData.entitlement_info.licenseMode.toLowerCase();
        }

        return appLicenseMode;
    }

    isFreemiumApp(appData) {
        let isFreemium = false;
        if (this.getLicenseMode(appData) === LICENSE_MODE.FREEMIUM) {
            isFreemium = true;
        }
        return isFreemium;
    }

    isAppInPlan(appData) {
        let appInPlan = true;
        const entitlementStatus = this.getEntitlementStatus(appData);
        if ((entitlementStatus === ENTITLEMENTS.TRIAL_AVAILABLE)
            || (entitlementStatus === ENTITLEMENTS.TRIAL_ACTIVE)
            || (entitlementStatus === ENTITLEMENTS.TRIAL_EXPIRED)
            || (entitlementStatus === ENTITLEMENTS.LICENSE_EXPIRED)
            || (entitlementStatus === ENTITLEMENTS.NO_ENTITLEMENT)) {
            appInPlan = false;
        }
        return appInPlan;
    }

    isLicenseExpired(appData) {
        let isLicenseExpired = false;
        const entitlementStatus = this.getEntitlementStatus(appData);
        if ((entitlementStatus === ENTITLEMENTS.TRIAL_EXPIRED)
            || (entitlementStatus === ENTITLEMENTS.LICENSE_EXPIRED)) {
            isLicenseExpired = true;
        }
        return isLicenseExpired;
    }

    isTrialActive(appData) {
        let isTrialActive = false;
        const entitlementStatus = this.getEntitlementStatus(appData);
        if (entitlementStatus === ENTITLEMENTS.TRIAL_ACTIVE) {
            isTrialActive = true;
        }
        return isTrialActive;
    }

    isTrialAvailable(appData) {
        let isTrialAvailable = false;
        const entitlementStatus = this.getEntitlementStatus(appData);
        if (entitlementStatus === ENTITLEMENTS.TRIAL_AVAILABLE) {
            isTrialAvailable = true;
        }
        return isTrialAvailable;
    }

    // #endregion

    // #region Update related functions
    getUpdateType(appData) {
        let updateType = '';
        if (this.isPropertyPresent(appData, 'update_type')) {
            updateType = appData.update_type;
        }
        return updateType;
    }

    isFullAppUpdate(appData) {
        return (this.getUpdateType(appData) === UPDATE_TYPE.FULL_APP_UPDATE);
    }

    isPatchUpdate(appData) {
        return (this.getUpdateType(appData) === UPDATE_TYPE.PATCH_UPDATE);
    }

    isFullBuildUpdate(appData) {
        return (this.getUpdateType(appData) === UPDATE_TYPE.FULL_BUILD_UPDATE);
    }

    isShownAsPatchUpdate(mode, appData) {
        let isShownAsPatchUpdate = false;
        if (mode === APP_MODE.AVAILABLE_UPDATES
        && (this.isPatchUpdate(appData) || this.isFullBuildUpdate(appData))) {
            isShownAsPatchUpdate = true;
        }
        return isShownAsPatchUpdate;
    }

    isUpdateInProgress({ mode, appData }) {
        let updateInProgress = false;
        if (mode === APP_MODE.ACTIVE_SESSIONS) {
            if (this.getActionInProgress(appData) === ACTION_IN_PROGRESS.UPDATE
            || ( // handling FAU install from other versions
                this.getActionInProgress(appData) === ACTION_IN_PROGRESS.INSTALL
                && this.isFullAppUpdate(appData)
            )) {
                updateInProgress = true;
            }
        }
        return updateInProgress;
    }
    // #endregion

    isAppInError(appData) {
        let appInError = false;
        if ((this.isPropertyPresent(appData, 'error_details')
        && this.isPropertyPresent(appData.error_details, 'error_code'))
        || ((this.isPropertyPresent(appData, 'errorDetails')
        && this.isPropertyPresent(appData.errorDetails, 'errorCode')))) {
            appInError = true;
        }
        return appInError;
    }

    isLaunchable(appData) {
        let isLaunchable = false;
        if (this.isPropertyPresent(appData, 'launchable')) {
            isLaunchable = (appData.launchable === 'true');
        }

        if (appData.channel === 'sti') {
        // || this.getEntitlementStatus(appData) === 'LOADING') {
            isLaunchable = false;
        }
        return isLaunchable;
    }

    isUninstallable(mode, appData) {
        let isUninstallable = true;
        if (mode === APP_MODE.AVAILABLE_UPDATES) {
            isUninstallable = !this.isFullAppUpdate(appData);
        }
        isUninstallable = isUninstallable && appData.uninstallable === 'true';
        return isUninstallable;
    }

    toPDPAppActionListModel({ mode, appData, appModel }) {
        let buttonList; let dropDownList;
        if (this.isFullAppUpdate(appData)) {
            ({ buttonList, dropDownList } = this.toUpdatePageAppActionListModel(
                appData,
                mode,
                appModel
            ));
        } else {
            ({ buttonList, dropDownList } = this.toDefaultPageAppActionListModel(
                appData,
                mode,
                appModel
            ));
        }

        // eslint-disable-next-line
        buttonList.forEach(button => {button.actionType = { ...button.actionType, component: 'button' }});
        // eslint-disable-next-line
        dropDownList.forEach(button => {button.actionType = { component: 'dropdown' }});
        return buttonList.concat(dropDownList);
    }

    getQuietButtonList(quietButtonList, appData) {
        const finalButtonList = [];

        quietButtonList.forEach((quietButtonItem) => {
            switch (quietButtonItem.actionId) {
            case ACTION_ID.OPEN:
                if (this.isLaunchable(appData) || (quietButtonItem.isDisabled && appData.channel !== 'sti')) {
                    finalButtonList.push(this.getTileAction(TILE_ACTION.OPEN));
                } else if (!this.isLaunchable(appData)
                    && appData.channel !== 'sti'
                    && !this.isFullAppUpdate(appData)) {
                    quietButtonItem.isDisabled = true; // eslint-disable-line no-param-reassign
                    finalButtonList.push(quietButtonItem);
                }
                break;
            case ACTION_ID.UPDATE:
                if (!this.isLaunchable(appData) && (appData.channel === 'sti' || this.isFullAppUpdate(appData))) {
                    finalButtonList.push(this.getTileAction(TILE_ACTION.UPDATE));
                }
                break;
            default:
                finalButtonList.push(quietButtonItem);
            }
        });
        return finalButtonList;
    }

    getSecondaryQuietButtonList(secondaryQuietButtonList, appData) {
        const finalButtonList = [];

        secondaryQuietButtonList.forEach((secondaryQuietButtonItem) => {
            switch (secondaryQuietButtonItem.actionId) {
            case ACTION_ID.DISCOVER:
                finalButtonList.push(this.getTileAction(TILE_ACTION.DISCOVER));
                break;
            case ACTION_ID.OPEN:
                if (this.isLaunchable(appData) || secondaryQuietButtonItem.isDisabled) {
                    finalButtonList.push(this.getTileAction(TILE_ACTION.OPEN));
                }
                break;
            default:
                finalButtonList.push(secondaryQuietButtonItem);
            }
        });
        return finalButtonList;
    }

    getDropDownItems(dropDownItemList, appData, mode, appModel) {
        const finalDropDownList = [];
        const shouldShowOtherversion = () => {
            if (appData
                && appData.other_version_data
                && appData.other_version_data.other_versions
                && appData.other_version_data.other_versions.length) {
                return true;
            }
            if (appModel
                    && appModel.otherVersionDetails
                    && appModel.otherVersionDetails.other_versions
                    && appModel.otherVersionDetails.other_versions.length) {
                return true;
            }
            return false;
        };
        const isProductLaunchable = () => {
            if (mode === APP_MODE.AVAILABLE_UPDATES) {
                return !this.isFullAppUpdate(appData) && appData.launchable === 'true';
            }
            return appData.launchable === 'true';
        };

        dropDownItemList.forEach((dropDownItem) => {
            switch (dropDownItem.actionId) {
            case ACTION_ID.UNINSTALL:
                if (this.isUninstallable(mode, appData)) {
                    finalDropDownList.push(this.getTileAction(TILE_ACTION.UNINSTALL));
                }
                break;
            case ACTION_ID.OPEN:
                if (isProductLaunchable() || dropDownItem.isDisabled) {
                    finalDropDownList.push(this.getTileAction(TILE_ACTION.OPEN));
                }
                break;
            case ACTION_ID.LIST_OTHER_VERSIONS:
                if (shouldShowOtherversion()) {
                    finalDropDownList.push(this.getTileAction(TILE_ACTION.LIST_OTHER_VERSIONS));
                }
                break;
            case ACTION_ID.LEARN_MORE:
                if (appData.channel !== 'sti') {
                    finalDropDownList.push(this.getTileAction(TILE_ACTION.LEARN_MORE));
                }
                break;
            case ACTION_ID.DISCOVER:
                finalDropDownList.push(this.getTileAction(TILE_ACTION.DISCOVER));
                break;
            default:
                finalDropDownList.push(dropDownItem);
            }
        });
        return finalDropDownList;
    }

    toUpdatePageAppActionListModel(appData, mode) {
        let tileActionObj = {
            quietButton: [],
            ctaButton: [],
            dropDownItem: [],
        };

        if (mode === APP_MODE.AVAILABLE_UPDATES) {
            tileActionObj = {
                quietButton: [],
                ctaButton: [],
                dropDownItem: [],
            };
            if (appData.tryUpdate && appData.tryUpdate === 'true') {
                tileActionObj.ctaButton.push(this.getTileAction(TILE_ACTION.TRY));
            } else {
                tileActionObj.ctaButton.push(this.getTileAction(TILE_ACTION.UPDATE));
            }
        } else if (mode === APP_MODE.ACTIVE_SESSIONS) {
            if (this.isAppInError(appData)) {
                tileActionObj = {
                    quietButton: [this.getTileAction(TILE_ACTION.RETRY)],
                    ctaButton: [],
                    dropDownItem: [],
                };
            } else {
                tileActionObj = {
                    quietButton: [],
                    ctaButton: [],
                    dropDownItem: [],
                };
                if (appData.tryUpdate && appData.tryUpdate === 'true') {
                    tileActionObj.ctaButton.push({ ...this.getTileAction(TILE_ACTION.TRY), isDisabled: true });
                } else {
                    tileActionObj.ctaButton.push({ ...this.getTileAction(TILE_ACTION.UPDATE), isDisabled: true });
                }
            }
        } else if (mode === APP_MODE.INSTALLED) { // For recently updated section
            tileActionObj = this.getInstalledAppActionObj(appData);
            // Remove DropDown list as it should not be dipalyed in updates page
            tileActionObj.dropDownItem = [];
            if (tileActionObj.secondaryQuietButton
                && tileActionObj.secondaryQuietButton.length
                && tileActionObj.secondaryQuietButton[0].actionId === ACTION_ID.DISCOVER) {
                tileActionObj.secondaryQuietButton = [];
            }
        } else if (mode === APP_MODE.INCOMPATIBLE_APPS) {
            tileActionObj = {
                primaryQuietButton: [this.getTileAction(TILE_ACTION.NOT_COMPATIBLE)],
            };
        }
        const actionLists = this.jsonToAppActionListModel(tileActionObj);
        return actionLists;
    }

    getTrialActiveActionObj(appData, mode, isActiveSession) {
        return {
            secondaryQuietButton: this.getSecondaryQuietButtonList([{ ...this.getTileAction(TILE_ACTION.OPEN), isDisabled: isActiveSession }], appData),
            ctaButton: [this.getTileAction(TILE_ACTION.BUY_NOW)],
            dropDownItem: this.getDropDownItems(
                [
                    this.getTileAction(TILE_ACTION.LEARN_MORE),
                    this.getTileAction(TILE_ACTION.DISCOVER),
                    this.getTileAction(TILE_ACTION.LIST_OTHER_VERSIONS),
                    { ...this.getTileAction(TILE_ACTION.UNINSTALL), isDisabled: isActiveSession },
                ], appData, mode
            ),
        };
    }

    getInstalledAppActionObj(appData, isActiveSession) {
        let tileActionObj;
        if (this.isLicenseExpired(appData)) {
            tileActionObj = {
                secondaryQuietButton: this.getSecondaryQuietButtonList([this.getTileAction(TILE_ACTION.DISCOVER)], appData),
                ctaButton: [this.getTileAction(TILE_ACTION.BUY_NOW)],
                dropDownItem: this.getDropDownItems(
                    [
                        this.getTileAction(TILE_ACTION.LEARN_MORE),
                        this.getTileAction(TILE_ACTION.LIST_OTHER_VERSIONS),
                        { ...this.getTileAction(TILE_ACTION.UNINSTALL), isDisabled: isActiveSession },
                    ], appData, APP_MODE.INSTALLED
                ),
            };
        } else if (this.isTrialActive(appData, isActiveSession)) {
            tileActionObj = this.getTrialActiveActionObj(appData, APP_MODE.INSTALLED, isActiveSession);
        } else if (this.isTrialAvailable(appData)) {
            tileActionObj = {
                secondaryQuietButton: [this.getTileAction(TILE_ACTION.BUY_NOW)],
                ctaButton: [{ ...this.getTileAction(TILE_ACTION.START_TRIAL), isDisabled: isActiveSession }],
                dropDownItem: this.getDropDownItems(
                    [
                        this.getTileAction(TILE_ACTION.LEARN_MORE),
                        this.getTileAction(TILE_ACTION.DISCOVER),
                        { ...this.getTileAction(TILE_ACTION.LIST_OTHER_VERSIONS), isDisabled: isActiveSession },
                        { ...this.getTileAction(TILE_ACTION.UNINSTALL), isDisabled: isActiveSession },
                    ], appData, APP_MODE.INSTALLED
                ),
            };
        } else {
            tileActionObj = {
                secondaryQuietButton: this.getSecondaryQuietButtonList([
                    this.getTileAction(TILE_ACTION.DISCOVER)], appData),
                quietButton: this.getQuietButtonList([{ ...this.getTileAction(TILE_ACTION.OPEN), isDisabled: isActiveSession }], appData),
                dropDownItem: this.getDropDownItems(
                    [
                        this.getTileAction(TILE_ACTION.LEARN_MORE),
                        { ...this.getTileAction(TILE_ACTION.LIST_OTHER_VERSIONS), isDisabled: isActiveSession },
                        { ...this.getTileAction(TILE_ACTION.UNINSTALL), isDisabled: isActiveSession },
                    ], appData, APP_MODE.INSTALLED
                ),
            };
        }
        return tileActionObj;
    }

    getInstalledAppActionObjForCategoryPage(appData, isActiveSession) {
        let tileActionObj;
        if (this.isLicenseExpired(appData)) {
            tileActionObj = {
                secondaryQuietButton: this.getSecondaryQuietButtonList([this.getTileAction(TILE_ACTION.LEARN_MORE)], appData),
                ctaButton: [this.getTileAction(TILE_ACTION.BUY_NOW)],
            };
        } else if (this.isTrialActive(appData, isActiveSession)) {
            tileActionObj = {
                secondaryQuietButton: this.getSecondaryQuietButtonList([{ ...this.getTileAction(TILE_ACTION.LEARN_MORE), isDisabled: isActiveSession }], appData),
                quietButton: [this.getTileAction(TILE_ACTION.OPEN)],
            };
        } else if (this.isTrialAvailable(appData)) {
            tileActionObj = {
                secondaryQuietButton: [this.getTileAction(TILE_ACTION.LEARN_MORE)],
                ctaButton: [{ ...this.getTileAction(TILE_ACTION.START_TRIAL), isDisabled: isActiveSession }],
            };
        } else {
            tileActionObj = {
                secondaryQuietButton: this.getSecondaryQuietButtonList([
                    this.getTileAction(TILE_ACTION.LEARN_MORE)], appData),
                quietButton: this.getQuietButtonList([{ ...this.getTileAction(TILE_ACTION.OPEN), isDisabled: isActiveSession }], appData),
            };
        }
        return tileActionObj;
    }

    getUpdateAvailableAppActionObj(appData, isActiveSession) {
        const mode = APP_MODE.AVAILABLE_UPDATES;
        const tileActionObj = {
            secondaryQuietButton: [],
            quietButton: [],
            ctaButton: [],
            dropDownItem: [],
        };


        if (this.isLicenseExpired(appData)) {
            tileActionObj.secondaryQuietButton = this.getSecondaryQuietButtonList([
                this.getTileAction(TILE_ACTION.DISCOVER)], appData);
            tileActionObj.ctaButton.push(this.getTileAction(TILE_ACTION.BUY_NOW));
            tileActionObj.dropDownItem = this.getDropDownItems(
                [
                    this.getTileAction(TILE_ACTION.LEARN_MORE),
                    { ...this.getTileAction(TILE_ACTION.LIST_OTHER_VERSIONS), isDisabled: isActiveSession },
                    { ...this.getTileAction(TILE_ACTION.UNINSTALL), isDisabled: isActiveSession },
                ], appData, mode
            );
        } else if (this.isTrialActive(appData)) {
            tileActionObj.secondaryQuietButton = this.getSecondaryQuietButtonList([
                { ...this.getTileAction(TILE_ACTION.OPEN), isDisabled: isActiveSession }], appData);
            tileActionObj.ctaButton.push(this.getTileAction(TILE_ACTION.BUY_NOW));
            tileActionObj.dropDownItem = this.getDropDownItems(
                [
                    this.getTileAction(TILE_ACTION.DISCOVER),
                    this.getTileAction(TILE_ACTION.LEARN_MORE),
                    { ...this.getTileAction(TILE_ACTION.LIST_OTHER_VERSIONS), isDisabled: isActiveSession },
                    { ...this.getTileAction(TILE_ACTION.UNINSTALL), isDisabled: isActiveSession },
                ], appData, mode
            );
        } else if (this.isTrialAvailable(appData)) {
            if (this.isFullAppUpdate(appData)) {
                tileActionObj.ctaButton.push(this.getTileAction(TILE_ACTION.BUY_NOW));
            } else {
                tileActionObj.secondaryQuietButton = this.getSecondaryQuietButtonList([
                    this.getTileAction(TILE_ACTION.BUY_NOW)], appData);
                tileActionObj.ctaButton.push({ ...this.getTileAction(TILE_ACTION.START_TRIAL), isDisabled: isActiveSession });
            }
            tileActionObj.dropDownItem = this.getDropDownItems(
                [
                    this.getTileAction(TILE_ACTION.DISCOVER),
                    this.getTileAction(TILE_ACTION.LEARN_MORE),
                    { ...this.getTileAction(TILE_ACTION.LIST_OTHER_VERSIONS), isDisabled: isActiveSession },
                    { ...this.getTileAction(TILE_ACTION.UNINSTALL), isDisabled: isActiveSession },
                ], appData, mode
            );
        } else {
            tileActionObj.secondaryQuietButton = this.getSecondaryQuietButtonList([
                this.getTileAction(TILE_ACTION.DISCOVER)], appData);
            tileActionObj.quietButton = this.getQuietButtonList([
                { ...this.getTileAction(TILE_ACTION.OPEN), isDisabled: isActiveSession },
                { ...this.getTileAction(TILE_ACTION.UPDATE), isDisabled: isActiveSession }], appData);
            tileActionObj.dropDownItem = this.getDropDownItems(
                [
                    this.getTileAction(TILE_ACTION.LEARN_MORE),
                    { ...this.getTileAction(TILE_ACTION.LIST_OTHER_VERSIONS), isDisabled: isActiveSession },
                    { ...this.getTileAction(TILE_ACTION.UNINSTALL), isDisabled: isActiveSession },
                ], appData, mode
            );
        }

        if (appData.tryUpdate && appData.tryUpdate === 'true') {
            tileActionObj.quietButton = [this.getTileAction(TILE_ACTION.TRY)];
        }

        return tileActionObj;
    }

    getUpdateAvailableAppActionObjForCategoryPage(appData, isActiveSession) {
        const tileActionObj = {
            secondaryQuietButton: [],
            quietButton: [],
            ctaButton: [],
            dropDownItem: [],
        };


        if (this.isLicenseExpired(appData)) {
            tileActionObj.secondaryQuietButton = this.getSecondaryQuietButtonList([
                this.getTileAction(TILE_ACTION.LEARN_MORE)], appData);
            tileActionObj.ctaButton.push(this.getTileAction(TILE_ACTION.BUY_NOW));
        } else if (this.isTrialActive(appData)) {
            tileActionObj.secondaryQuietButton = this.getSecondaryQuietButtonList([
                this.getTileAction(TILE_ACTION.LEARN_MORE)], appData);
            tileActionObj.quietButton.push({ ...this.getTileAction(TILE_ACTION.OPEN), isDisabled: isActiveSession });
        } else if (this.isTrialAvailable(appData)) {
            tileActionObj.secondaryQuietButton = this.getSecondaryQuietButtonList([
                this.getTileAction(TILE_ACTION.LEARN_MORE)], appData);
            if (this.isFullAppUpdate(appData)) {
                tileActionObj.ctaButton.push(this.getTileAction(TILE_ACTION.BUY_NOW));
            } else {
                tileActionObj.ctaButton.push({ ...this.getTileAction(TILE_ACTION.START_TRIAL), isDisabled: isActiveSession });
            }
        } else {
            tileActionObj.secondaryQuietButton = this.getSecondaryQuietButtonList([
                this.getTileAction(TILE_ACTION.LEARN_MORE)], appData);
            tileActionObj.quietButton = this.getQuietButtonList([
                { ...this.getTileAction(TILE_ACTION.OPEN), isDisabled: isActiveSession },
                { ...this.getTileAction(TILE_ACTION.UPDATE), isDisabled: isActiveSession }], appData);
        }

        if (appData.tryUpdate && appData.tryUpdate === 'true') {
            tileActionObj.quietButton = [this.getTileAction(TILE_ACTION.TRY)];
        }

        return tileActionObj;
    }

    toDefaultPageAppActionListModel(appData, mode, appModel) {
        let tileActionObj = {
            quietButton: [],
            ctaButton: [],
            dropDownItem: [],
            secondaryQuietButton: [],
        };
        if (mode === APP_MODE.AVAILABLE_TO_INSTALL) {
            tileActionObj = {
                secondaryQuietButton: [],
                quietButton: [],
                dropDownItem: this.getDropDownItems(
                    [
                        this.getTileAction(TILE_ACTION.LIST_OTHER_VERSIONS),
                    ], appData, mode, appModel
                ),
            };
            if (this.isAppInPlan(appData) || this.isFreemiumApp(appData)) {
                tileActionObj.secondaryQuietButton = this.getSecondaryQuietButtonList([this.getTileAction(TILE_ACTION.LEARN_MORE)]);
                tileActionObj.quietButton.push(this.getTileAction(TILE_ACTION.INSTALL));
            } else {
                tileActionObj.secondaryQuietButton = this.getSecondaryQuietButtonList([this.getTileAction(TILE_ACTION.LEARN_MORE)]);
                tileActionObj.quietButton.push(this.getTileAction(TILE_ACTION.TRY));
                tileActionObj.quietButton.push(this.getTileAction(TILE_ACTION.BUY));
            }
        } else if (mode === APP_MODE.AVAILABLE_UPDATES) {
            tileActionObj = this.getUpdateAvailableAppActionObj(appData);
        } else if (mode === APP_MODE.INSTALLED) {
            tileActionObj = this.getInstalledAppActionObj(appData);
        } else if (mode === APP_MODE.INCOMPATIBLE_APPS || mode === APP_MODE.INCOMPATIBLE) {
            tileActionObj = {
                secondaryQuietButton: this.getSecondaryQuietButtonList([this.getTileAction(TILE_ACTION.LEARN_MORE)], appData),
                primaryQuietButton: [this.getTileAction(TILE_ACTION.NOT_COMPATIBLE)],
                dropDownItem: this.getDropDownItems(
                    [
                        this.getTileAction(TILE_ACTION.LIST_OTHER_VERSIONS),
                    ], appData, mode, appModel
                ),
            };
        } else if (mode === APP_MODE.ACTIVE_SESSIONS) {
            if (this.isAppInError(appData)) {
                tileActionObj = {
                    quietButton: [this.getTileAction(TILE_ACTION.RETRY)],
                    dropDownItem: this.getDropDownItems(
                        [
                            this.getTileAction(TILE_ACTION.LIST_OTHER_VERSIONS),
                        ], appData, mode, appModel
                    ),
                };
            } else if (this.isUpdateInProgress({ mode, appData })) {
                if (this.isPatchUpdate(appData)) {
                    tileActionObj = this.getUpdateAvailableAppActionObj(appData, true);
                } else {
                    tileActionObj = {
                        secondaryQuietButton: this.getSecondaryQuietButtonList([this.getTileAction(TILE_ACTION.DISCOVER)], appData),
                        quietButton: [{ ...this.getTileAction(TILE_ACTION.UPDATE), isDisabled: true }],
                        dropDownItem: [this.getTileAction(TILE_ACTION.LEARN_MORE)],
                    };
                }
            } else if (this.isInstallInProgress({ mode, appData })) {
                tileActionObj = {
                    secondaryQuietButton: [],
                    quietButton: [],
                    dropDownItem: [],
                };
                if (this.isAppInPlan(appData) || this.isFreemiumApp(appData)) {
                    tileActionObj.secondaryQuietButton = this.getSecondaryQuietButtonList([this.getTileAction(TILE_ACTION.DISCOVER)], appData);
                    tileActionObj.quietButton.push({ ...this.getTileAction(TILE_ACTION.INSTALL), isDisabled: true });
                    tileActionObj.dropDownItem.push(this.getTileAction(TILE_ACTION.LEARN_MORE));
                } else {
                    tileActionObj.secondaryQuietButton = this.getSecondaryQuietButtonList([this.getTileAction(TILE_ACTION.LEARN_MORE)], appData);
                    tileActionObj.quietButton.push({ ...this.getTileAction(TILE_ACTION.TRY), isDisabled: true });
                    tileActionObj.quietButton.push(this.getTileAction(TILE_ACTION.BUY));
                }
            }
        } else if (mode === APP_MODE.WEB_APPS) {
            tileActionObj = {
                secondaryQuietButton: this.getSecondaryQuietButtonList([this.getTileAction(TILE_ACTION.LEARN_MORE)]),
                quietButton: [this.getTileAction(TILE_ACTION.LAUNCH_SERVICE)],
            };
        } else if (mode === APP_MODE.MOBILE_APPS) {
            tileActionObj = {
                secondaryQuietButton: this.getSecondaryQuietButtonList([this.getTileAction(TILE_ACTION.LEARN_MORE)]),
                quietButton: [this.getTileAction(TILE_ACTION.GET_MOBILE_LINK)],
            };
        }
        const actionLists = this.jsonToAppActionListModel(tileActionObj, appData, appModel);
        return actionLists;
    }

    toCategoryPageAppActionListModel(appData, mode, appModel) {
        let tileActionObj = {
            quietButton: [],
            ctaButton: [],
            dropDownItem: [],
            secondaryQuietButton: [],
        };
        if (mode === APP_MODE.AVAILABLE_TO_INSTALL) {
            tileActionObj = {
                secondaryQuietButton: [],
                quietButton: [],
                dropDownItem: this.getDropDownItems(
                    [
                        this.getTileAction(TILE_ACTION.LIST_OTHER_VERSIONS),
                    ], appData, mode, appModel
                ),
            };
            if (this.isAppInPlan(appData) || this.isFreemiumApp(appData)) {
                tileActionObj.secondaryQuietButton = this.getSecondaryQuietButtonList([this.getTileAction(TILE_ACTION.LEARN_MORE)]);
                tileActionObj.quietButton.push(this.getTileAction(TILE_ACTION.INSTALL));
            } else {
                tileActionObj.secondaryQuietButton = this.getSecondaryQuietButtonList([this.getTileAction(TILE_ACTION.LEARN_MORE)]);
                tileActionObj.quietButton.push(this.getTileAction(TILE_ACTION.TRY));
                tileActionObj.quietButton.push(this.getTileAction(TILE_ACTION.BUY));
            }
        } else if (mode === APP_MODE.AVAILABLE_UPDATES) {
            tileActionObj = this.getUpdateAvailableAppActionObjForCategoryPage(appData);
        } else if (mode === APP_MODE.INSTALLED) {
            tileActionObj = this.getInstalledAppActionObjForCategoryPage(appData);
        } else if (mode === APP_MODE.INCOMPATIBLE_APPS || mode === APP_MODE.INCOMPATIBLE) {
            tileActionObj = {
                secondaryQuietButton: this.getSecondaryQuietButtonList([
                    this.getTileAction(TILE_ACTION.LEARN_MORE)], appData),
                primaryQuietButton: [this.getTileAction(TILE_ACTION.NOT_COMPATIBLE)],
            };
        } else if (mode === APP_MODE.ACTIVE_SESSIONS) {
            if (this.isAppInError(appData)) {
                tileActionObj = {
                    quietButton: [this.getTileAction(TILE_ACTION.RETRY)],
                };
            } else if (this.isUpdateInProgress({ mode, appData })) {
                if (this.isPatchUpdate(appData)) {
                    tileActionObj = this.getUpdateAvailableAppActionObjForCategoryPage(appData, true);
                } else {
                    tileActionObj = {
                        secondaryQuietButton: this.getSecondaryQuietButtonList([this.getTileAction(TILE_ACTION.LEARN_MORE)], appData),
                        ctaButton: [{ ...this.getTileAction(TILE_ACTION.UPDATE), isDisabled: true }],
                    };
                }
            } else if (this.isInstallInProgress({ mode, appData })) {
                tileActionObj = {
                    secondaryQuietButton: [],
                    quietButton: [],
                    dropDownItem: [],
                };
                if (this.isAppInPlan(appData) || this.isFreemiumApp(appData)) {
                    tileActionObj.secondaryQuietButton = this.getSecondaryQuietButtonList([this.getTileAction(TILE_ACTION.LEARN_MORE)], appData);
                    tileActionObj.quietButton.push({ ...this.getTileAction(TILE_ACTION.INSTALL), isDisabled: true });
                } else {
                    tileActionObj.secondaryQuietButton = this.getSecondaryQuietButtonList([this.getTileAction(TILE_ACTION.LEARN_MORE)], appData);
                    tileActionObj.quietButton.push({ ...this.getTileAction(TILE_ACTION.TRY), isDisabled: true });
                    tileActionObj.quietButton.push(this.getTileAction(TILE_ACTION.BUY));
                }
            }
        } else if (mode === APP_MODE.WEB_APPS) {
            tileActionObj = {
                secondaryQuietButton: this.getSecondaryQuietButtonList([this.getTileAction(TILE_ACTION.LEARN_MORE)]),
                quietButton: [this.getTileAction(TILE_ACTION.LAUNCH_SERVICE)],
            };
        } else if (mode === APP_MODE.MOBILE_APPS) {
            tileActionObj = {
                secondaryQuietButton: this.getSecondaryQuietButtonList([this.getTileAction(TILE_ACTION.LEARN_MORE)]),
                quietButton: [this.getTileAction(TILE_ACTION.GET_MOBILE_LINK)],
            };
        }
        const actionLists = this.jsonToAppActionListModel(tileActionObj, appData, appModel);
        return actionLists;
    }

    toAppStatusModel({ mode, appData }) {
        let progressObj = null;
        if (mode === APP_MODE.ACTIVE_SESSIONS) {
            if (!this.isAppInError(appData)) {
                progressObj = this.toProgressModel(appData, mode);
            }
        }
        return progressObj;
    }

    hasCategory(appData, categoryID) {
        let hasCategory = false;
        if (this.isPropertyPresent(appData, 'categories')) {
            let categoriesData = [];
            if (appData.categories && Array.isArray(appData.categories)) {
                categoriesData = appData.categories;
            }
            const categoriesFilter = categoriesData.filter(obj => obj.name === categoryID);
            hasCategory = (categoriesFilter.length > 0);
        }
        return hasCategory;
    }

    addAllAppsFilters({ mode, appData, applicableFilters }) {
        if (mode === APP_MODE.INCOMPATIBLE_APPS && this.getInstalledVersionsForUpdate(appData).length > 0) {
            return;
        }
        applicableFilters.push(LEFT_NAV_KEYS.ALL_APPS_KEY);
    }

    addUpdateFilters({ mode, appData, applicableFilters }) {
        if (mode === APP_MODE.AVAILABLE_UPDATES
            || this.isUpdateInProgress({ mode, appData })) {
            applicableFilters.push(LEFT_NAV_KEYS.UPDATES_KEY);
            applicableFilters.push(UPDATES_APP_FILTERS.NEW_UPDATES);
            if (this.isFullAppUpdate(appData)) {
                applicableFilters.push(UPDATES_APP_FILTERS.NEW_VERSION_UPDATE);
            } else if (this.isPatchUpdate(appData)) {
                applicableFilters.push(UPDATES_APP_FILTERS.PATCH_UPDATE);
            }
        }
        if (this.isAppUpdatedRecently({ mode, appData })) {
            applicableFilters.push(LEFT_NAV_KEYS.UPDATES_KEY);
            applicableFilters.push(UPDATES_APP_FILTERS.UPDATED_RECENTLY);
        }
        if (mode === APP_MODE.INCOMPATIBLE_APPS) {
            if (this.getInstalledVersionsForUpdate(appData).length > 0) {
                applicableFilters.push(LEFT_NAV_KEYS.UPDATES_KEY);
                applicableFilters.push(UPDATES_APP_FILTERS.NEW_UPDATES);
            }
        }
    }

    addCategoriesFilters({ appData, applicableFilters }) {
        if (this.isPropertyPresent(appData, 'categories')) {
            let categoriesData = [];
            if (appData.categories && Array.isArray(appData.categories)) {
                categoriesData = appData.categories;
            }
            const categoriesFilter = categoriesData.map(obj => obj.name);
            applicableFilters.push(...categoriesFilter);
        }
    }

    addPlatformFilters({ mode, appData, applicableFilters }) {
        applicableFilters.push(this.getPlatformFilterValue({ mode, appData }));
    }

    addEntitlementFilters({ mode, appData, applicableFilters }) {
        const appPlatform = this.getAppPlatform({ mode });
        const entitlementStatus = this.getEntitlementStatus(appData);

        if (appPlatform === APP_PLATFORM.DESKTOP) {
            if (this.isAppInPlan(appData)) {
                applicableFilters.push(DESKTOP_APP_FILTERS.SUBSCRIBED);
            } else if (entitlementStatus === ENTITLEMENTS.TRIAL_AVAILABLE) {
                applicableFilters.push(DESKTOP_APP_FILTERS.TRIAL_AVAILABLE);
            } else if (entitlementStatus === ENTITLEMENTS.TRIAL_EXPIRED) {
                applicableFilters.push(DESKTOP_APP_FILTERS.TRIAL_EXPIRED);
            } else if (entitlementStatus === ENTITLEMENTS.TRIAL_ACTIVE) {
                applicableFilters.push(DESKTOP_APP_FILTERS.TRIAL_ACTIVE);
            }

            applicableFilters.push(this.getLicenseMode(appData));
        }
    }

    isStartTrialInProgress(mode, appData) {
        let startTrialInProgress = false;
        if (mode === APP_MODE.ACTIVE_SESSIONS) {
            const action = this.getActionInProgress(appData);
            if (action === ACTION_IN_PROGRESS.START_TRIAL) {
                startTrialInProgress = true;
            }
        }
        return startTrialInProgress;
    }

    isSessionSimulated({ mode, appData }) {
        let isSessionSimulated = false;
        if (mode === APP_MODE.ACTIVE_SESSIONS) {
            const action = this.getActionInProgress(appData);
            if (action === ACTION_IN_PROGRESS.SIMULATED) {
                isSessionSimulated = true;
            }
        }
        return isSessionSimulated;
    }

    addInstalledAppsFilters({ mode, appData, applicableFilters }) {
        const appPlatform = this.getAppPlatform({ mode });
        if (appPlatform === APP_PLATFORM.DESKTOP) {
            if (mode === APP_MODE.INSTALLED
                || this.isStartTrialInProgress(mode, appData)
                || mode === APP_MODE.AVAILABLE_UPDATES
                || this.isUpdateInProgress({ mode, appData })
                || this.isUninstallInProgress({ mode, appData })
                || this.isSessionSimulated({ mode, appData })
                || this.isPatchInstallInProgress({ mode, appData })) {
                applicableFilters.push(DESKTOP_APP_FILTERS.INSTALLED);
            } else {
                applicableFilters.push(DESKTOP_APP_FILTERS.NOT_INSTALLED);
            }
        } else {
            applicableFilters.push(DESKTOP_APP_FILTERS.NOT_INSTALLED);
        }
    }

    addCompatibilityFilter({ mode, appData, applicableFilters }) {
        const { isIncompatible } = this.getIncompatibilityDetails({ appData, mode });
        if (!isIncompatible) {
            applicableFilters.push(DESKTOP_APP_FILTERS.COMPATIBLE);
        }
    }

    getApplicableFilters({ mode, appData }) {
        const applicableFilters = [];
        this.addAllAppsFilters({ mode, appData, applicableFilters });
        this.addUpdateFilters({ mode, appData, applicableFilters });
        this.addCategoriesFilters({ mode, appData, applicableFilters });
        this.addPlatformFilters({ mode, appData, applicableFilters });
        this.addEntitlementFilters({ mode, appData, applicableFilters });
        this.addInstalledAppsFilters({ mode, appData, applicableFilters });
        this.addCompatibilityFilter({ mode, appData, applicableFilters });
        return applicableFilters;
    }

    toProgressModel(appData, mode) {
        let progressObj = null;
        const progress = this.getProgressData(appData, mode);
        if (progress) {
            progressObj = new Progress(progress);
        }
        return progressObj;
    }

    isUninstallInProgress({ mode, appData }) {
        let uninstallInProgress = false;
        if (mode === APP_MODE.ACTIVE_SESSIONS) {
            if (this.getActionInProgress(appData) === ACTION_IN_PROGRESS.UNINSTALL) {
                uninstallInProgress = true;
            }
        }
        return uninstallInProgress;
    }

    isInstallInProgress({ mode, appData }) {
        let installInProgress = false;
        if (mode === APP_MODE.ACTIVE_SESSIONS) {
            if (this.getActionInProgress(appData) === ACTION_IN_PROGRESS.INSTALL) {
                installInProgress = true;
            }
        }
        return installInProgress;
    }

    // This returns true if either patch install or full version install is in progress
    isPatchInstallInProgress({ mode, appData }) {
        let isPatchInstallInProgress = false;
        if (this.isInstallInProgress({ mode, appData }) && this.getInstalledVersion({ mode, appData }) !== '') {
            isPatchInstallInProgress = true;
        }
        return isPatchInstallInProgress;
    }

    getActionInProgress(appData) {
        let command = '';
        if (this.isPropertyPresent(appData, 'command')) {
            ({ command } = appData);
        } else if (this.isPropertyPresent(appData, 'action')) {
            ({ action: command } = appData);
        }
        return command;
    }

    actionCommandToStatusString(appData, getLower = false) {
        const command = this.getActionInProgress(appData);
        let commandStatusString = '';
        if (!command) {
            return commandStatusString;
        }
        switch (command.toLowerCase()) {
        case ACTION_IN_PROGRESS.INSTALL:
            if (getLower === true) {
                commandStatusString = TranslationUtil.getMessage('ccx.appAction.status.installing.lower');
            } else {
                commandStatusString = TranslationUtil.getMessage('ccx.appAction.status.installing');
            }
            break;
        case ACTION_IN_PROGRESS.UPDATE:
            if (getLower === true) {
                commandStatusString = TranslationUtil.getMessage('ccx.appAction.status.updating.lower');
            } else {
                commandStatusString = TranslationUtil.getMessage('ccx.appAction.status.updating');
            }
            break;
        case ACTION_IN_PROGRESS.UNINSTALL:
            if (getLower === true) {
                commandStatusString = TranslationUtil.getMessage('ccx.appAction.status.uninstalling.lower');
            } else {
                commandStatusString = TranslationUtil.getMessage('ccx.appAction.status.uninstalling');
            }
            break;
        default:
            break;
        }
        return commandStatusString;
    }

    getProgressData(appData) {
        let progress = {};
        progress.isCancellable = appData.cancelable === 'true';
        progress.cancelCallback = appData.cancelCallback;
        progress.localizedMessageText = appData.localizedMessageText;
        progress.actionInProgress = this.getActionInProgress(appData);
        progress.status = appData.status;

        if (appData.status && this.isPropertyPresent(appData, 'status')) {
            switch (appData.status.toLowerCase()) {
            case ACTIVE_SESSIONS_STATUS.CANCELLING:
                progress.localizedMessageText = TranslationUtil.getMessage('ccx.appAction.status.cancelling');
                progress.isCancellable = false;
                progress.inProgress = false;
                progress.isWaiting = true;
                break;
            case ACTIVE_SESSIONS_STATUS.COMPLETED:
                progress.localizedMessageText = TranslationUtil.getMessage('ccx.appAction.status.finishing');
                progress.inProgress = false;
                progress.isWaiting = true;
                break;
            case ACTIVE_SESSIONS_STATUS.WAITING:
                progress.localizedMessageText = TranslationUtil.getMessage('ccx.appAction.status.waiting');
                progress.inProgress = false;
                progress.isWaiting = true;
                break;
            case ACTIVE_SESSIONS_STATUS.EXTRACTING:
                progress.localizedMessageText = TranslationUtil.getMessage('ccx.appAction.status.extracting');
                progress.inProgress = false;
                progress.isWaiting = true;
                break;
            case ACTIVE_SESSIONS_STATUS.SIMULATED:
                progress.localizedMessageText = TranslationUtil.getMessage('ccx.appAction.status.waiting');
                progress.inProgress = false;
                progress.isCancellable = false;
                progress.isWaiting = true;
                break;
            case ACTIVE_SESSIONS_STATUS.INPROGRESS:
                progress.localizedMessageText = this.actionCommandToStatusString(appData);
                if (progress.localizedMessageText === '') {
                    progress = null;
                    return progress;
                }
                progress.inProgress = true;
                progress.isWaiting = false;
                if (this.isPropertyPresent(appData, 'totalProgressPercent')
                    && !Number.isNaN(appData.totalProgressPercent)) {
                    progress.percentage = appData.totalProgressPercent / 100;
                } else if (this.isPropertyPresent(appData, 'total_progress_percent')
                    && !Number.isNaN(appData.total_progress_percent)) {
                    progress.percentage = appData.total_progress_percent / 100;
                } else {
                    progress = null;
                }
                break;
            default: {
                progress = null;
            }
            }
        } else {
            progress = null;
        }
        return progress;
    }

    getPlatformFilterValue({ mode }) {
        let appPlatformFilter = PLATFORM_FILTERS.DESKTOP;
        const appPlatform = this.getAppPlatform({ mode });
        if (appPlatform === APP_PLATFORM.WEB) {
            appPlatformFilter = PLATFORM_FILTERS.WEB;
        } else if (appPlatform === APP_PLATFORM.MOBILE) {
            appPlatformFilter = PLATFORM_FILTERS.MOBILE;
        } else if (appPlatform === APP_PLATFORM.DESKTOP) {
            appPlatformFilter = PLATFORM_FILTERS.DESKTOP;
        }
        return appPlatformFilter;
    }

    getDaysElapsed(updatedTimeStamp) {
        const MILLISEC_PER_DAY = 60 * 60 * 24;
        const currentTimeStamp = Math.floor((new Date()).getTime() / 1000);
        return Math.floor((currentTimeStamp - updatedTimeStamp) / MILLISEC_PER_DAY);
    }

    isAppUpdatedRecently({ mode, appData }) {
        let updatedRecently = false;
        if (mode === APP_MODE.INSTALLED) {
            if (this.isPropertyPresent(appData, 'lastModifiedTimeStamp') && this.isPropertyPresent(appData, 'lastModifiedOperationType')) {
                const daysElapsed = this.getDaysElapsed(appData.lastModifiedTimeStamp);
                if ((appData.lastModifiedOperationType === MODIFYIING_OPERATION_TYPES.PATCH_UPDATE
                    || appData.lastModifiedOperationType === MODIFYIING_OPERATION_TYPES.FULL_APP_UPDATE)
                    && daysElapsed <= APPS_CONFIG.APP_RECENTLY_UPDATED_THRESHOLD) {
                    updatedRecently = true;
                }
            }
        }
        return updatedRecently;
    }

    getAppSubHeadingFilters({ mode, appData }) {
        const appSubHeadingFilters = [];
        this.addAllAppsSubHeadingFilters({ mode, appData, appSubHeadingFilters });
        this.addCategoriesSubHeadingFilters({ mode, appData, appSubHeadingFilters });
        this.addUpdatesSubHeadingFilters({ mode, appData, appSubHeadingFilters });
        return appSubHeadingFilters;
    }

    showUpToDateForInstalledApp(appData) {
        if (this.isLaunchable(appData) === false
        && (this.isAppInPlan(appData) || this.isTrialActive(appData)
        || this.isFreemiumApp(appData))) {
            return true;
        }
        return false;
    }

    getLabelList({ mode, appData }) {
        const labelList = [];
        if ((appData.channel === 'sti'
            // || this.getEntitlementStatus(appData) === 'LOADING'
            || this.showUpToDateForInstalledApp(appData)
        ) && mode === APP_MODE.INSTALLED) {
            labelList.push(new AppLabel({
                labelId: LABEL_VARIANT_TYPE.UPTO_DATE,
                icon: ICON_VARIANT_TYPE.CHECKMARK_CIRCLE,
                localizedText: TranslationUtil.getMessage('ccx.app.label.uptoDate'),
            }));
        }
        return labelList;
    }

    getErrorDetails(appData) {
        let errorDetails = null;
        if (this.isAppInError(appData)) {
            let errorCode = '';
            let customerSupportLink = '';
            let errorSummaryLink = '';
            if (this.isPropertyPresent(appData, 'error_details')
            && this.isPropertyPresent(appData.error_details, 'error_code')) {
                errorCode = appData.error_details.error_code;
            }
            if (this.isPropertyPresent(appData, 'errorDetails')) {
                if (this.isPropertyPresent(appData.errorDetails, 'errorCode')) {
                    ({ errorCode } = appData.errorDetails);
                }
                if (this.isPropertyPresent(appData.errorDetails, 'customerSupportLink')) {
                    ({ customerSupportLink } = appData.errorDetails);
                }
                if (this.isPropertyPresent(appData.errorDetails, 'errorSummaryLink')) {
                    ({ errorSummaryLink } = appData.errorDetails);
                }
            }
            errorDetails = {
                errorCode,
                customerSupportLink,
                errorSummaryLink,
                errorAction: this.getActionInProgress(appData),
            };
        }
        return errorDetails;
    }

    getAppInfoDialogActionList({ mode, appData }) {
        const actionList = [];
        if (mode === APP_MODE.AVAILABLE_TO_INSTALL
            || ((mode === APP_MODE.AVAILABLE_UPDATES) && this.isFullAppUpdate(appData))) {
            actionList.push({
                actionId: APPINFO_ACTION_ID.INSTALL,
                localizedText: TranslationUtil.getMessage('ccx.appAction.install'),
                ariaLabel: 'Install',
            });
        }
        if ((mode === APP_MODE.INSTALLED || this.isShownAsPatchUpdate(mode, appData))
            && this.isUninstallable(mode, appData)) {
            actionList.push({
                actionId: APPINFO_ACTION_ID.UNINSTALL,
                localizedText: TranslationUtil.getMessage('ccx.appAction.uninstall'),
                ariaLabel: 'Uninstall',
            });
        }
        if (mode === APP_MODE.INCOMPATIBLE_APPS) {
            actionList.push({
                actionId: APPINFO_ACTION_ID.NOT_COMPATIBLE,
                localizedText: TranslationUtil.getMessage('ccx.appAction.notCompatible'),
                ariaLabel: 'Not Compatible',
            });
        }
        return actionList;
    }

    getActionList(inAppData) {
        let actionListForAppInfo = this.getAppInfoDialogActionList(inAppData);
        actionListForAppInfo = [...actionListForAppInfo,
            ...this.toPDPAppActionListModel(inAppData)];
        return actionListForAppInfo;
    }

    getOtherVersionDetails({ appData }) {
        let otherVersionDetails = {};
        if (this.isPropertyPresent(appData, 'other_version_data')) {
            otherVersionDetails = appData.other_version_data;
        }
        if (this.isPropertyPresent(appData, 'legacy_products_url')) {
            otherVersionDetails.legacyProductURL = '';
            otherVersionDetails.legacyProductURL = appData.legacy_products_url;
        }
        return otherVersionDetails;
    }

    isOlderApp(appData) {
        if (appData.EOL && appData.EOL === 'true') {
            return true;
        }
        return false;
    }

    getAppLineage(appData) {
        return appData.app_lineage;
    }


    isAppLaunchable(mode, appData) {
        let isAppLaunchable = false;
        if ((mode === APP_MODE.AVAILABLE_UPDATES) || (mode === APP_MODE.INSTALLED)) {
            if (!this.isLicenseExpired(appData) && !this.isTrialAvailable(appData) && this.isLaunchable(appData)) {
                isAppLaunchable = true;
            }
        }
        return isAppLaunchable;
    }

    getInstalledVersion(mode, appData) {
        let installedVersion = '';
        if (mode === APP_MODE.AVAILABLE_UPDATES || mode === APP_MODE.ACTIVE_SESSIONS) {
            if (appData.installed_version) {
                installedVersion = appData.installed_version;
            }
        }
        if (mode === APP_MODE.INSTALLED) {
            installedVersion = appData.version;
        }
        return installedVersion;
    }

    getInstalledVersionsForUpdate(appData) {
        let installedVersionsForUpdate = '';
        if (appData.installed_versions_for_update) {
            installedVersionsForUpdate = appData.installed_versions_for_update;
        }
        return installedVersionsForUpdate;
    }

    getAllInstalledVersions(mode, appData) {
        let allInstalledVersions = '';
        if (appData.all_installed_versions) {
            allInstalledVersions = appData.all_installed_versions;
        }
        return allInstalledVersions;
    }

    getLatestVersion(appData) {
        if (appData.latest_version) {
            return appData.latest_version;
        }
        return appData.version;
    }

    getFirstInstallTriggerTimeStamp(appData) {
        if (this.isPropertyPresent(appData, 'firstInstallTriggerTimeStamp')) {
            return appData.firstInstallTriggerTimeStamp;
        }
        return '';
    }

    getLastModifiedTimeStamp(appData) {
        if (this.isPropertyPresent(appData, 'lastModifiedTimeStamp')) {
            return appData.lastModifiedTimeStamp;
        }
        return '';
    }

    getOSPlatform(appData) {
        if (this.isPropertyPresent(appData, 'platform')) {
            return appData.platform;
        }
        return '';
    }

    toAppModel(inAppData) {
        const { appData, mode } = inAppData;
        const { sapcode } = appData;
        const codexVersion = appData.version;
        const installedVersion = this.getInstalledVersion(mode, appData);
        const updateType = this.getUpdateType(appData, mode);
        const installedVersionsForUpdate = this.getInstalledVersionsForUpdate(appData);
        const allInstalledVersions = this.getAllInstalledVersions(mode, appData);
        const isUpdateAvailable = this.isUpdateAvailable(inAppData);
        const appFamilyName = this.getFamilyName(inAppData);
        const platform = this.getAppPlatform(inAppData);
        const supportedOS = this.getSupportedOS(inAppData);
        const iconURL = this.getAppIcon(inAppData);
        const backUpIcon = this.getBackUpIcon(inAppData);
        const { isIncompatible, incompatibilityReason } = this.getIncompatibilityDetails(inAppData);
        const systemRequirementURL = this.getSystemRequirementURL(inAppData);
        const errorDetails = this.getErrorDetails(appData);
        const isEOLApp = this.isOlderApp(appData);
        const applicableFilters = this.getApplicableFilters(inAppData);
        const sortOrder = this.getSortOrder(inAppData);
        const appSectionBasedFields = this.toAppSectionBasedFields(
            applicableFilters, inAppData.appData, mode
        );

        const progress = this.toAppStatusModel(inAppData);
        const { onAppAction } = inAppData;
        const labelList = this.getLabelList(inAppData);
        const productId = appData.sapcode;
        const actionIds = {
            select: ACTION_ID.APP_TILE_CLICK,
        }; // TODO
        const latestVersion = this.getLatestVersion(appData);
        const actionList = this.getActionList(inAppData); // all applicable actions for this app
        const otherVersionDetails = this.getOtherVersionDetails(inAppData);
        const appLineage = this.getAppLineage(appData);
        const isUninstallable = appData.uninstallable;
        const isLaunchable = this.isAppLaunchable(mode, appData);
        const entitlementInfo = appData.entitlement_info;
        const isHidden = (appData.hidden === 'true');
        const productWhatsNewUrl = this.getProductWhatsNewUrl(appData);
        const firstInstallTriggerTimeStamp = this.getFirstInstallTriggerTimeStamp(appData);
        const lastModifiedTimeStamp = this.getLastModifiedTimeStamp(appData);
        const isAppInError = this.isAppInError(appData);
        const errorAction = this.getActionInProgress(appData);
        const osPlatform = this.getOSPlatform(appData);
        const isSupportedForLongTerm = (appData.isMaintenanceBuild === 'true');
        return new App({
            sapcode,
            codexVersion,
            isUpdateAvailable,
            installedVersion,
            installedVersionsForUpdate,
            allInstalledVersions,
            platform,
            supportedOS,
            iconURL,
            backUpIcon,
            appFamilyName,
            isIncompatible,
            incompatibilityReason,
            errorDetails,
            appSectionBasedFields,
            progress,
            updateType,
            appSectionFilter: applicableFilters,
            subSectionFilter: applicableFilters,
            subHeadingFilter: applicableFilters,
            onAppAction,
            actionIds,
            sortOrder,
            actionList,
            productId,
            labelList,
            otherVersionDetails,
            isEOLApp,
            systemRequirementURL,
            appLineage,
            isUninstallable,
            isLaunchable,
            entitlementInfo,
            isHidden,
            mode,
            latestVersion,
            productWhatsNewUrl,
            firstInstallTriggerTimeStamp,
            lastModifiedTimeStamp,
            isAppInError,
            errorAction,
            osPlatform,
            isSupportedForLongTerm,
        });
    }

    // #region Subtext related functions
    resetSubText(appModel) {
        appModel.appSectionBasedFields.forEach((appSectionBasedField) => {
            appSectionBasedField.setSubText(null);
        });
    }

    updateSubtext(appModel, appData, mode) {
        appModel.appSectionBasedFields.forEach((appSectionBasedField) => {
            if (appSectionBasedField.appSectionFilter.includes(LEFT_NAV_KEYS.UPDATES_KEY)) {
                appSectionBasedField.setSubText(
                    this.getAppSubtext(appData, LEFT_NAV_KEYS.UPDATES_KEY, mode)
                );
            } else {
                appSectionBasedField.setSubText(
                    this.getAppSubtext(appData, 'default')
                );
            }
        });
    }

    updateStatusLabels(appModel, appData, mode) {
        appModel.appSectionBasedFields.forEach((appSectionBasedField) => {
            appSectionBasedField.setStatusLabelList(
                this.getStatusLabelList(appData, mode)
            );
        });
    }

    getFailureSubtext(appData) {
        let subtext = null;
        if (this.isAppInError(appData)) {
            const actionCommandToOperationFailedString = () => {
                const command = this.getActionInProgress(appData);
                let operationFailedString = '';
                switch (command && command.toLowerCase()) {
                case ACTION_IN_PROGRESS.INSTALL:
                    operationFailedString = TranslationUtil.getMessage('ccx.command.install.error');
                    break;
                case ACTION_IN_PROGRESS.UPDATE:
                    operationFailedString = TranslationUtil.getMessage('ccx.command.update.error');
                    break;
                case ACTION_IN_PROGRESS.UNINSTALL:
                    operationFailedString = TranslationUtil.getMessage('ccx.command.uninstall.error');
                    break;
                default:
                    operationFailedString = TranslationUtil.getMessage('ccx.command.default.error');
                }
                return operationFailedString;
            };
            subtext = new AppStatusLabel({
                type: 'error',
                errorType: 'sessionError',
                priorityType: LABEL_PRIORITY_TYPES.SESSION_ERROR,
                localizedText: actionCommandToOperationFailedString(appData),
                variant: 'negative',
                action: new AppAction({
                    actionId: ACTION_ID.ERROR_INFO,
                    actionType: {
                        quiet: true,
                        variant: 'link',
                    },
                    localizedText: TranslationUtil.getMessage('ccx.command.error.more.info'),
                }),
            });
        }
        return subtext;
    }

    getIconAriaLabelText() {
        const desktopIcon = TranslationUtil.getMessage('ccx.appTile.platformIcons.desktop.tooltip');
        const mobileIcon = TranslationUtil.getMessage('ccx.appTile.platformIcons.mobile.tooltip');
        const webIcon = TranslationUtil.getMessage('ccx.appTile.platformIcons.web.tooltip');
        const iconLabel = TranslationUtil.getMessage('ccx.appTile.platformIcons.icon.tooltip');
        return { desktopLabel: `${desktopIcon} ${iconLabel}`, mobileLabel: `${mobileIcon} ${iconLabel}`, webLabel: `${webIcon} ${iconLabel}` };
    }

    getUpdatedSubtext(appData, mode) {
        let subtext = null;
        let subTextStr = '';
        if (this.isAppUpdatedRecently({ mode, appData })) {
            const daysElapsed = this.getDaysElapsed(appData.lastModifiedTimeStamp);
            if (daysElapsed === 0) {
                subTextStr = TranslationUtil.getMessage('ccx.subtext.recentlyUpdated.today');
            } else if (daysElapsed === 1) {
                subTextStr = TranslationUtil.getMessage('ccx.subtext.recentlyUpdated.oneDay');
            } else {
                subTextStr = TranslationUtil.getMessage('ccx.subtext.recentlyUpdated', { days: daysElapsed });
            }
            subtext = {
                type: 'info',
                localizedText: subTextStr,
            };
        } else if (this.isFullAppUpdate(appData)) {
            subTextStr = TranslationUtil.getMessage('ccx.subtext.newRelease');
            subtext = {
                type: 'info',
                localizedText: subTextStr,
            };
        }
        return subtext;
    }

    getEntitlementSubtext(appData, mode) {
        let subtext = null;

        if (mode && (mode === APP_MODE.AVAILABLE_TO_INSTALL || mode === APP_MODE.ACTIVE_SESSIONS)) {
            return subtext;
        }

        if (this.isLicenseExpired(appData)) {
            subtext = new AppStatusLabel({
                type: 'error',
                localizedText: TranslationUtil.getMessage('ccx.subtext.trialExpired'),
                priorityType: LABEL_PRIORITY_TYPES.TRIAL_EXPIRED,
                variant: 'negative',
            });
        }

        if (this.isTrialActive(appData)) {
            const trialDaysRemaining = parseInt(this.getTrialDaysRemaining({ appData }), 10);
            let localizedText;
            const type = (trialDaysRemaining > 5) ? 'info' : 'error';
            const priorityType = (trialDaysRemaining > 5)
                ? LABEL_PRIORITY_TYPES.TRIAL_DAYS_REMAINING_GT_5 : LABEL_PRIORITY_TYPES.TRIAL_DAYS_REMAINING_LT_5;
            if (trialDaysRemaining === 1) {
                localizedText = TranslationUtil.getMessage('ccx.subtext.1DayRemaining');
            } else if (trialDaysRemaining > 1) {
                localizedText = TranslationUtil.getMessage('ccx.subtext.nDaysRemaining', { days: trialDaysRemaining });
            }

            subtext = new AppStatusLabel({
                type,
                localizedText,
                priorityType,
                variant: 'notice',
            });
        }
        return subtext;
    }

    getAppSubtext(appData, appSection, mode) {
        let subtext;
        if (appSection === LEFT_NAV_KEYS.UPDATES_KEY) {
            if (!subtext) {
                subtext = this.getUpdatedSubtext(appData, mode);
            }
        }
        return subtext;
    }

    addUpdateStatusLabel(appData, labelList) {
        if (this.isUpdateAvailable({ appData })
        || this.isFullAppUpdate(appData) || this.isPatchUpdate(appData) || this.isFullBuildUpdate(appData)) {
            labelList.push(new AppStatusLabel({
                localizedText: TranslationUtil.getMessage('ccx.appTile.update.updateAvailable'),
                variant: 'info',
                action: new AppAction({
                    actionId: ACTION_ID.UPDATE_STATUS,
                }),
            }));
        } else if (!labelList.length) {
            labelList.push(new AppStatusLabel({
                localizedText: TranslationUtil.getMessage('ccx.appTile.update.upToDate'),
                variant: 'positive',
            }));
        }
    }

    getStatusLabelList(appData, mode) {
        const labelList = [];
        const failureLabel = this.getFailureSubtext(appData);
        if (failureLabel) {
            labelList.push(failureLabel);
        }
        if (!labelList.length) {
            const entitlementLabel = this.getEntitlementSubtext(appData, mode);
            if (entitlementLabel) {
                labelList.push(entitlementLabel);
            }
        }
        if (mode === APP_MODE.INSTALLED
            || (mode === APP_MODE.AVAILABLE_UPDATES)) {
            this.addUpdateStatusLabel(appData, labelList);
        }
        return labelList;
    }

    // #endregion

    updateErrorDetailsOnFailure(appModel, appData) {
        appModel.setErrorDetails(this.getErrorDetails(appData));
    }

    updateWithProgressPacket(inAppModel, progressData) {
        const appModel = inAppModel;
        const mode = APP_MODE.ACTIVE_SESSIONS;
        if (!this.isAppInError(progressData)) {
            const progress = this.getProgressData(progressData);
            if (progress) {
                ProgressBarAnalytics.sendProgressAnalytics(inAppModel, progressData);
                appModel.updateProgress(progress.inProgress, progress.isWaiting,
                    progress.percentage, progress.localizedMessageText,
                    progress.isCancellable, progress.status, progress.actionInProgress);
                this.resetSubText(inAppModel);
                this.disableActionsBlockedByProgess(appModel);
            }
        } else {
            this.updateSubtext(appModel, progressData);
            this.updateStatusLabels(appModel, progressData, mode);
            this.updateErrorDetailsOnFailure(appModel, progressData);
            appModel.appSectionBasedFields.forEach((appSectionBasedField) => {
                this.updateButtonsForSectionBasedField(appSectionBasedField,
                    inAppModel,
                    mode,
                    inAppModel);
            });
            appModel.resetProgress();
            /* skipOnErrorActionListUpdate is added to avoid applicable action list update for appmodel
               In CCW applicable action list is set at the time of app model creation and doesn't require updation in that.
               The flag is set from CCW
             */
            if (!progressData.skipOnErrorActionListUpdate) {
                const actionList = this.toPDPAppActionListModel({
                    mode,
                    appData: inAppModel,
                    appModel: inAppModel,
                });
                appModel.setActionList(actionList);
            }
            this.updatePDPModel(appModel, null);
        }
    }

    disableActionsBlockedByProgess(appModel) {
        appModel.appSectionBasedFields.forEach((appSectionBasedField) => {
            const disabledButtonList = appSectionBasedField.buttonList.map((action) => {
                if (DISABLED_ACTIONS_IN_PROGRESS.includes(action.actionId)) {
                    if (action.actionType && !action.actionType.isDisabled) {
                        return {
                            ...action,
                            actionType: {
                                ...action.actionType,
                                isDisabled: true,
                            },
                        };
                    }
                }
                return action;
            });
            appSectionBasedField.setButtonList(disabledButtonList);
            const disabledDropDownList = appSectionBasedField.dropDownList.map((action) => {
                if (DISABLED_ACTIONS_IN_PROGRESS.includes(action.actionId)) {
                    if (action.actionType && !action.actionType.isDisabled) {
                        return {
                            ...action,
                            actionType: {
                                ...action.actionType,
                                isDisabled: true,
                            },
                        };
                    }
                }
                return action;
            });
            appSectionBasedField.setDropDownList(disabledDropDownList);
        });
    }

    updateButtonsForSectionBasedField(appSectionBasedField, appData, mode, appModel) {
        let buttonList = [];
        let dropDownList = [];
        if (appSectionBasedField.appSectionFilter.includes(LEFT_NAV_KEYS.UPDATES_KEY)) {
            // eslint-disable-next-line max-len
            ({ buttonList, dropDownList } = this.toUpdatePageAppActionListModel(appData, mode, appModel));
        } else {
            // eslint-disable-next-line max-len
            ({ buttonList, dropDownList } = this.toDefaultPageAppActionListModel(appData, mode, appModel));
        }
        appSectionBasedField.setButtonList(buttonList);
        appSectionBasedField.setDropDownList(dropDownList);
    }

    getSeeMoreLink() {
        const tileActionObj = {
            linkItem: [this.getTileAction(TILE_ACTION.SEE_MORE)],
        };
        const { linksList } = this.jsonToAppActionListModel(tileActionObj);
        return linksList[0];
    }

    hasSeeMoreURL(appData) {
        let showSeeMoreLink = '';
        if ((this.getProductWhatsNewUrl(appData))
        && (this.isFullAppUpdate(appData) || this.isPatchUpdate(appData) || this.isFullBuildUpdate(appData)
         || this.getLastUpdateType(appData) === UPDATE_TYPE.FULL_APP_UPDATE
         || this.getLastUpdateType(appData) === UPDATE_TYPE.FULL_BUILD_UPDATE
         || this.getLastUpdateType(appData) === UPDATE_TYPE.PATCH_UPDATE
        )) {
            showSeeMoreLink = 'true';
        }
        return showSeeMoreLink;
    }

    getProductWhatsNewUrl(appData) {
        let productWhatsNewUrl = '';
        if (this.isPropertyPresent(appData, 'product_whatsnew_url')) {
            productWhatsNewUrl = appData.product_whatsnew_url;
        }
        return productWhatsNewUrl;
    }

    getLastUpdateType(appData) {
        let lastUpdateType = '';
        if (this.isPropertyPresent(appData, 'lastModifiedOperationType')) {
            lastUpdateType = appData.lastModifiedOperationType;
        }
        return lastUpdateType;
    }

    toUpdateSectionFieldModel(filtersList, appData, mode) {
        const appSectionFilter = filtersList;
        const localizedTagline = '';
        const localizedUpdateDescription = this.getLocalizedUpdateDescription(appData, mode);
        const { buttonList, dropDownList } = this.toUpdatePageAppActionListModel(appData, mode);
        const seeMoreLink = this.getSeeMoreLink();
        const seeMoreLinkUrl = this.hasSeeMoreURL(appData);
        const subText = this.getAppSubtext(appData, LEFT_NAV_KEYS.UPDATES_KEY, mode);
        const isHeaderClickable = false;
        const displayLearnMoreIcon = false;
        const lastUpdateType = this.getLastUpdateType(appData, mode);
        const updateTileVersion = appData.version;
        const statusLabelList = this.getStatusLabelList(appData, mode);
        return new AppSectionBasedField({
            appSectionFilter,
            localizedTagline,
            subText,
            localizedUpdateDescription,
            buttonList,
            dropDownList,
            seeMoreLink,
            seeMoreLinkUrl,
            isHeaderClickable,
            displayLearnMoreIcon,
            lastUpdateType,
            updateTileVersion,
            statusLabelList,
            showStatusLabel: true,
            showSessionErrorOnly: true,
            showDropDown: false,
        });
    }

    /**
     * getUpdateField
     * public api for getting updateField from appModel
     * @param {object}
     * @return {object}
     */
    getUpdateField = (appModel) => {
        const { appSectionBasedFields } = appModel;
        const updateFields = appSectionBasedFields
            .filter(field => field.appSectionFilter.includes(LEFT_NAV_KEYS.UPDATES_KEY));
        let updateField = null;
        if (updateFields.length > 0) {
            [updateField] = updateFields;
        }
        return updateField;
    }

    getDefaultField = (appModel) => {
        const { appSectionBasedFields } = appModel;
        const defaultField = appSectionBasedFields
            .filter(field => !field.appSectionFilter.includes(LEFT_NAV_KEYS.UPDATES_KEY))[0];
        return defaultField;
    }

    getTileVersionLabel(appData, mode) {
        if (this.isFullAppUpdate(appData)) {
            if (!this.getInstalledVersion(mode, appData)) {
                return TranslationUtil.getMessage('ccx.appTile.label.new');
            }
        }
        return null;
    }

    toDefaultSectionFieldModel(filtersList, appData, mode) {
        const appSectionFilter = [...filtersList, 'pdp'];
        const localizedTagline = this.getLocalizedTagline(appData);
        const localizedUpdateDescription = '';
        let buttonList;
        let dropDownList;
        if (appSectionFilter.includes(CATEGORY_IDS.PRE_RELEASE) || appSectionFilter.includes(CATEGORY_IDS.BETA_APPS)) {
            ({ buttonList, dropDownList } = this.toDefaultPageAppActionListModel(appData, mode));
        } else {
            ({ buttonList, dropDownList } = this.toCategoryPageAppActionListModel(appData, mode));
        }
        const subText = this.getAppSubtext(appData, LEFT_NAV_KEYS.ALL_APPS_KEY, mode);
        const iconText = this.getIconAriaLabelText();
        const showDropDown = appSectionFilter.includes(CATEGORY_IDS.PRE_RELEASE)
        || appSectionFilter.includes(CATEGORY_IDS.BETA_APPS);
        const showSessionErrorOnly = !appSectionFilter.includes(CATEGORY_IDS.BETA_APPS)
        && !appSectionFilter.includes(CATEGORY_IDS.PRE_RELEASE);
        const statusLabelList = this.getStatusLabelList(appData, mode);
        const isCategorySection = !appSectionFilter.includes(CATEGORY_IDS.BETA_APPS)
        && !appSectionFilter.includes(CATEGORY_IDS.PRE_RELEASE);
        return new AppSectionBasedField({
            appSectionFilter,
            localizedTagline,
            subText,
            localizedUpdateDescription,
            buttonList,
            dropDownList,
            iconText,
            showStatusLabel: true,
            isCardClickable: true,
            showDropDown,
            showSessionErrorOnly,
            statusLabelList,
            isCategorySection,
        });
    }

    toAllAppsSectionFieldModel(filtersList, appData, mode) {
        const appSectionFilter = [...filtersList, 'pdp'];
        const localizedTagline = this.getLocalizedTagline(appData);
        const localizedUpdateDescription = '';
        const { buttonList, dropDownList } = this.toDefaultPageAppActionListModel(appData, mode);
        const subText = this.getAppSubtext(appData, LEFT_NAV_KEYS.ALL_APPS_KEY, mode);
        const iconText = this.getIconAriaLabelText();
        const statusLabelList = this.getStatusLabelList(appData, mode);
        return new AppSectionBasedField({
            appSectionFilter,
            localizedTagline,
            subText,
            localizedUpdateDescription,
            buttonList,
            dropDownList,
            iconText,
            showStatusLabel: true,
            statusLabelList,
        });
    }


    toAppSectionBasedFields(appSectionFilters, appData, mode) {
        const appSectionBasedFieldsGroup = {
            updates: [],
            all: [],
            default: [],
        };
        const appSectionBasedFields = [];
        appSectionFilters.forEach((appSectionFilter) => {
            if (appSectionFilter === LEFT_NAV_KEYS.UPDATES_KEY) {
                appSectionBasedFieldsGroup.updates.push(appSectionFilter);
            } else if (appSectionFilter === LEFT_NAV_KEYS.ALL_APPS_KEY) {
                appSectionBasedFieldsGroup.all.push(appSectionFilter);
            } else {
                appSectionBasedFieldsGroup.default.push(appSectionFilter);
            }
        });

        Object.keys(appSectionBasedFieldsGroup).forEach((filter) => {
            const filters = appSectionBasedFieldsGroup[filter];
            if (filter === LEFT_NAV_KEYS.UPDATES_KEY) {
                if (filters.length > 0) {
                    const updateSection = this.toUpdateSectionFieldModel(filters, appData, mode);
                    appSectionBasedFields.push(updateSection);
                }
            }
            if (filter === LEFT_NAV_KEYS.ALL_APPS_KEY) {
                if (filters.length > 0) {
                    const allAppsSection = this.toAllAppsSectionFieldModel(filters, appData, mode);
                    appSectionBasedFields.push(allAppsSection);
                }
            }
            if (filter === 'default') {
                if (filters.length > 0) {
                    const defaultSection = this.toDefaultSectionFieldModel(filters, appData, mode);
                    appSectionBasedFields.push(defaultSection);
                }
            }
        });
        return appSectionBasedFields;
    }

    isLearnMoreVisible(dropDownList) {
        const learnMoreButtons = dropDownList.filter(dropDownItem => (dropDownItem.actionId
            === ACTION_ID.LEARN_MORE));
        if (learnMoreButtons.length) {
            return true;
        }
        return false;
    }

    showLearnMore(appModel) {
        appModel.updateActionHiddenStatus(ACTION_ID.LEARN_MORE, false);
    }

    hideLearnMore(appModel) {
        appModel.updateActionHiddenStatus(ACTION_ID.LEARN_MORE, true);
    }

    updatePDPModel(appModel, allAppsModel) {
        if (PDPs.getPDP(appModel.sapcode)) {
            const pdpModel = PDPs.getPDP(appModel.sapcode);
            let appModelToReplace;
            if (pdpModel.app.codexVersion === appModel.codexVersion) {
                appModelToReplace = appModel;
            } else if (allAppsModel) {
                const appsWithSapcode = allAppsModel
                    .filter(appObject => appObject.sapcode === pdpModel.app.sapcode);
                const appsWithSapcodeAndVersion = appsWithSapcode
                    .filter(appObject => appObject.codexVersion === pdpModel.app.codexVersion);
                if (!appsWithSapcodeAndVersion.length && appsWithSapcode.length) {
                    if (!appsWithSapcode[0].subSectionFilter.includes(APP_MODE.INSTALLED)
                        || appsWithSapcode[0].isPatchUpdate()) {
                        [appModelToReplace] = appsWithSapcode;
                    } else {
                        // Set install cta in case one is uninsalled and
                        // when more than one verison is installed
                        appModelToReplace = {
                            ...pdpModel.app,
                            actionList: [{
                                ...this.getTileAction(TILE_ACTION.INSTALL),
                                actionType: { ...this.toAppActionTypeModel('ctaButton'), component: 'button' },
                            }],
                        };
                        if (!appModelToReplace.appSectionBasedFields) {
                            LoggerUtil.error(`${APPS_COMPONENT_ID}: ${COMPONENT_ID}: updatePDPModel: App model missing. Should not happen.`);
                            return;
                        }
                        appModelToReplace.appSectionBasedFields
                            .forEach((appSectionBasedField) => {
                                appSectionBasedField
                                    .setButtonList([{
                                        ...this.getTileAction(TILE_ACTION.INSTALL),
                                        actionType: { ...this.toAppActionTypeModel('ctaButton'), component: 'button' },
                                    }]);
                            });
                    }
                }
            }
            if (appModelToReplace) {
                pdpModelMapper.refreshAppModelData(pdpModel,
                    { appObject: appModelToReplace });
            }
        }
    }

    getDefaultCTAId(appModel) {
        const defaultSectionBasedField = appModel.getDefaultSectionBasedField();
        if (defaultSectionBasedField && defaultSectionBasedField.buttonList.length) {
            return defaultSectionBasedField.buttonList[0].actionId;
        }
        if (appModel.mode === APP_MODE.ACTIVE_SESSIONS
            && appModel.progress
            && appModel.progress.actionInProgress === ACTION_IN_PROGRESS.UPDATE) {
            return PROGRESS_UPDATE_CTA_ID;
        }
        LoggerUtil.warn(`${APPS_COMPONENT_ID}: ${COMPONENT_ID}: No CTA found for app with model:${JSON.stringify(appModel)}`);
        return null;
    }


    removeListOtherVersions(appModel) {
        const defaultSectionBasedField = appModel.getDefaultSectionBasedField();
        const listOtherVersionActions = defaultSectionBasedField
            .dropDownList
            .filter(action => action.actionId === ACTION_ID.LIST_OTHER_VERSIONS);
        if (listOtherVersionActions.length) {
            defaultSectionBasedField.removeDropDownItem(listOtherVersionActions[0]);
        }
    }
}
export default new AppModelMapper();
